import React from "react";
import {
  ArrowLeft,
  Shield,
  AlertCircle,
  CheckCircle,
  Clock,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";
import { useNavigate } from "react-router-dom";

const TermsConditionsPage = () => {
  const lastUpdated = "January 15, 2025";
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      <HeaderComponent />
      <section className="py-12 px-4 bg-gradient-to-r from-orange-500 to-red-500 text-white">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate("/")}
              className="flex items-center text-white hover:text-orange-100 transition-all mr-6"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </button>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-2xl">
              🪔
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4">Terms & Conditions</h1>
          <p className="text-xl">
            Please read these terms carefully before using MahalaxmiPuja
            services
          </p>
          <p className="text-sm mt-4 opacity-90">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            {/* Introduction */}
            <div className="mb-12 p-6 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start">
                <Shield className="h-6 w-6 text-orange-500 mr-3 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Welcome to MahalaxmiPuja
                  </h3>
                  <p className="text-gray-600">
                    These Terms and Conditions govern your use of MahalaxmiPuja
                    platform and services. By accessing or using our services,
                    you agree to be bound by these terms.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-10">
              {/* Section 1 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    1
                  </span>
                  Acceptance of Terms
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    By accessing MahalaxmiPuja platform, you acknowledge that
                    you have read, understood, and agree to be bound by these
                    Terms and Conditions and our Privacy Policy.
                  </p>
                  <p>
                    You must be at least 18 years old to independently book our
                    services. Users under 18 may use our services with parent or
                    guardian consent and supervision.
                  </p>
                  <p>
                    We reserve the right to modify these terms at any time.
                    Continued use of our services constitutes acceptance of
                    updated terms.
                  </p>
                </div>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    2
                  </span>
                  Service Description
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    MahalaxmiPuja provides online temple puja booking services,
                    connecting devotees with authentic Vedic rituals performed
                    at Shree Mahalaxmi Ambabai Temple, Kolhapur.
                  </p>
                  <p>Our services include:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>
                      Traditional puja ceremonies performed by experienced
                      pandits
                    </li>
                    <li>Video recordings of personalized rituals</li>
                    <li>Blessed prasad and sacred items delivery</li>
                    <li>Customer support for spiritual guidance</li>
                  </ul>
                  <p>
                    All pujas are performed following traditional Vedic
                    practices with complete devotion and authenticity.
                  </p>
                </div>
              </div>

              {/* Section 3 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    3
                  </span>
                  Booking and Payment
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    <strong>Booking Process:</strong> All bookings must be made
                    through our official platform with complete and accurate
                    information.
                  </p>
                  <p>
                    <strong>Payment:</strong> We accept only online payments.
                  </p>
                  <p>
                    <strong>Pricing:</strong> All prices are listed in Indian
                    Rupees (INR) and include applicable taxes. Additional
                    courier charges may apply for prasad delivery.
                  </p>
                  <p>
                    <strong>Booking Confirmation:</strong> You will receive
                    booking confirmation via email within 30 minutes of
                    successful payment.
                  </p>
                </div>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    4
                  </span>
                  Service Delivery
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    <strong>Puja Performance:</strong> All pujas will be
                    performed on the scheduled date and time by qualified
                    pandits at our temple premises.
                  </p>
                  <p>
                    <strong>Video Delivery:</strong> Video recordings will be
                    shared within 24-48 hours of puja completion via WhatsApp or
                    email.
                  </p>
                  <p>
                    <strong>Prasad Delivery:</strong> Blessed prasad and sacred
                    items will be delivered to your provided address within 3-7
                    working days across India.
                  </p>
                  <p>
                    <strong>Service Area:</strong> We currently provide services
                    within India only. International delivery is not available
                    at this time.
                  </p>
                </div>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    5
                  </span>
                  User Responsibilities
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>You agree to:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>
                      Provide accurate and complete information during booking
                    </li>
                    <li>
                      Use our services for legitimate spiritual purposes only
                    </li>
                    <li>
                      Respect the sanctity and traditions of temple services
                    </li>
                    <li>Make payments on time for confirmed bookings</li>
                    <li>
                      Be available for communication regarding your booking
                    </li>
                    <li>
                      Treat our staff and pandits with respect and courtesy
                    </li>
                  </ul>
                  <p>
                    You are responsible for ensuring the accuracy of your
                    contact details and delivery address.
                  </p>
                </div>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    6
                  </span>
                  Cancellation and Refunds
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    <strong>Cancellation Policy:</strong> You may cancel your
                    booking any time before the scheduled puja date for a full
                    refund.
                  </p>
                  <p>
                    <strong>No Refund After Puja:</strong> Once the puja has
                    been performed, no refunds will be provided as the spiritual
                    service has been completed.
                  </p>
                  <p>
                    <strong>Refund Processing:</strong> Approved refunds will be
                    processed within 3 working days to your original payment
                    method.
                  </p>
                  <p>
                    For detailed refund terms, please refer to our Refund Policy
                    page.
                  </p>
                </div>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    7
                  </span>
                  Limitation of Liability
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    MahalaxmiPuja services are provided for spiritual and
                    religious purposes. We do not guarantee specific outcomes or
                    results from puja ceremonies.
                  </p>
                  <p>
                    We are not liable for any indirect, incidental, or
                    consequential damages arising from the use of our services.
                  </p>
                  <p>
                    Our liability is limited to the amount paid for the specific
                    service in question.
                  </p>
                  <p>
                    We are not responsible for delays caused by circumstances
                    beyond our control (natural disasters, government
                    restrictions, etc.).
                  </p>
                </div>
              </div>

              {/* Section 8 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    8
                  </span>
                  Intellectual Property
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    All content on MahalaxmiPuja platform, including videos,
                    images, and text, is protected by copyright and other
                    intellectual property laws.
                  </p>
                  <p>
                    You may not reproduce, distribute, or use our content for
                    commercial purposes without written permission.
                  </p>
                  <p>
                    Video recordings of your personalized pujas are provided for
                    personal spiritual use only and should not be shared
                    publicly without consent.
                  </p>
                </div>
              </div>

              {/* Section 9 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    9
                  </span>
                  Privacy and Data Protection
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    We collect and use your personal information only as
                    necessary to provide our temple services effectively.
                  </p>
                  <p>
                    Your data will not be shared with third parties or used for
                    marketing purposes without your explicit consent.
                  </p>
                  <p>
                    For detailed information about data collection and usage,
                    please refer to our Privacy Policy.
                  </p>
                </div>
              </div>

              {/* Section 10 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    10
                  </span>
                  Dispute Resolution
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    We encourage resolving any disputes through direct
                    communication with our customer support team first.
                  </p>
                  <p>
                    Any legal disputes arising from these terms will be subject
                    to the exclusive jurisdiction of courts in Kolhapur,
                    Maharashtra, India.
                  </p>
                  <p>These terms are governed by the laws of India.</p>
                </div>
              </div>

              {/* Section 11 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    11
                  </span>
                  Termination
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    We reserve the right to suspend or terminate access to our
                    services for users who violate these terms or engage in
                    inappropriate behavior.
                  </p>
                  <p>
                    You may discontinue using our services at any time, subject
                    to our cancellation and refund policies.
                  </p>
                </div>
              </div>

              {/* Section 12 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    12
                  </span>
                  Contact Information
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    If you have any questions about these Terms and Conditions,
                    please contact us:
                  </p>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="font-medium">Phone:</span>
                        <span className="ml-2">+91 9699588995</span>
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="font-medium">Email:</span>
                        <span className="ml-2">
                          ambabaimahalaxmipuja@gmail.com
                        </span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="font-medium">Address:</span>
                        <span className="ml-2">
                          Shree Mahalaxmi Ambabai Temple, Kolhapur, Maharashtra
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Notice */}
            <div className="mt-12 p-6 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold mb-2">Important Notice</h4>
                  <p className="text-sm">
                    By using MahalaxmiPuja services, you acknowledge that you
                    have read, understood, and agree to these Terms and
                    Conditions. These terms constitute a legally binding
                    agreement between you and MahalaxmiPuja.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default TermsConditionsPage;
